/**
 * `MockDataEditor` — shared editor for the mock learner data blob
 * (`MockLearnerData` in the sandbox API).
 *
 * Extracted from `TemplatePromptSandbox.tsx` so the new
 * `/evaluations/profiles` editor page can render the same UI without
 * duplicating the biographic-info + custom-fields form. The template
 * sandbox and the profile editor both mount this component; state
 * lives in the parent.
 */
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import type { FC } from "react";
import { useState } from "react";

import type { BiographicInfo, MockLearnerData } from "@api/rest/prompt-sandbox";

interface Props {
  value: MockLearnerData;
  onChange: (v: MockLearnerData) => void;
}

export const MockDataEditor: FC<Props> = ({ value, onChange }) => {
  const bio = value.biographic_info ?? {};

  // Represent custom_fields as an ordered array of [key, value] pairs so
  // rows have stable identity while the user edits them.
  const [customRows, setCustomRows] = useState<{ key: string; val: string }[]>(() =>
    Object.entries(value.custom_fields ?? {}).map(([key, val]) => ({ key, val })),
  );

  const pushCustomRows = (rows: { key: string; val: string }[]) => {
    setCustomRows(rows);
    const record: Record<string, string> = {};
    for (const r of rows) {
      if (r.key.trim()) record[r.key.trim()] = r.val;
    }
    onChange({ ...value, custom_fields: Object.keys(record).length ? record : undefined });
  };

  const setBio = (patch: Partial<BiographicInfo>) => onChange({ ...value, biographic_info: { ...bio, ...patch } });

  return (
    <div className="border border-border-subtle rounded-md overflow-hidden">
      <div className="p-3 flex flex-col gap-2 bg-bg-surface">
        <div className="flex gap-2">
          <label className="flex flex-col gap-1 flex-1 min-w-0">
            <span className="text-xs text-text-tertiary">Name</span>
            <input
              type="text"
              className="text-xs rounded border border-border-subtle bg-bg-default px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-limestone-900"
              value={bio.name ?? ""}
              onChange={(e) => setBio({ name: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 w-16 shrink-0">
            <span className="text-xs text-text-tertiary">Age</span>
            <input
              type="text"
              className="text-xs rounded border border-border-subtle bg-bg-default px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-limestone-900"
              value={bio.age ?? ""}
              onChange={(e) => setBio({ age: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 w-20 shrink-0">
            <span className="text-xs text-text-tertiary">Gender</span>
            <input
              type="text"
              className="text-xs rounded border border-border-subtle bg-bg-default px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-limestone-900"
              value={bio.gender ?? ""}
              onChange={(e) => setBio({ gender: e.target.value })}
            />
          </label>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-text-tertiary">Date of Report</span>
          <input
            type="text"
            className="text-xs rounded border border-border-subtle bg-bg-default px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-limestone-900"
            value={value.date_of_report ?? ""}
            onChange={(e) => onChange({ ...value, date_of_report: e.target.value })}
          />
        </label>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-tertiary">Input Fields</span>
            <button
              type="button"
              onClick={() => pushCustomRows([...customRows, { key: "", val: "" }])}
              className="flex items-center gap-0.5 text-xs text-text-tertiary hover:text-text-secondary transition-colors"
            >
              <PlusIcon className="size-3" />
              Add field
            </button>
          </div>
          {customRows.length === 0 ? (
            <p className="text-xs text-text-tertiary italic">
              No input fields. Add key-value pairs to inject extra context into prompts.
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {customRows.map((row, i) => (
                // Stack key + value vertically instead of side-by-side so
                // value textareas can grow into the row's full width — the
                // narrow inline textarea was cramped for JSON blobs and
                // multi-paragraph notes.
                <div key={i} className="flex flex-col gap-1 p-2 rounded border border-border-subtle bg-bg-default">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="key"
                      value={row.key}
                      onChange={(e) => {
                        const next = customRows.map((r, j) => (j === i ? { ...r, key: e.target.value } : r));
                        pushCustomRows(next);
                      }}
                      className="text-xs flex-1 min-w-0 rounded border border-border-subtle bg-bg-surface px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-limestone-900 font-mono"
                    />
                    <button
                      type="button"
                      aria-label={`Remove ${row.key || "field"}`}
                      onClick={() => pushCustomRows(customRows.filter((_, j) => j !== i))}
                      className="p-1.5 rounded text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors shrink-0"
                    >
                      <TrashIcon className="size-4" />
                    </button>
                  </div>
                  <textarea
                    rows={4}
                    placeholder="value (supports multi-line / JSON)"
                    value={row.val}
                    onChange={(e) => {
                      const next = customRows.map((r, j) => (j === i ? { ...r, val: e.target.value } : r));
                      pushCustomRows(next);
                    }}
                    className="text-xs rounded border border-border-subtle bg-bg-surface px-2 py-1.5 resize-y min-h-[6rem] font-mono focus:outline-none focus:ring-1 focus:ring-limestone-900"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
